/* Public read-only Pitch Deck viewer.
   Loaded at /p/:deckId — no authentication required. */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Sparkle, FileText, ChartLineUp, FilePdf } from "@phosphor-icons/react";
import { extractErrorMessage } from "@/lib/apiError";
import { ExportProgressOverlay } from "@/components/ProgressDonut";

const C = { bgDark: "#000", panel: "#0A2826", panelHover: "#0D3330", teal: "#0A8B85", tealSoft: "#7BD3CE", orange: "#F59E0B" };

const ClinqMark = () => (
  <span className="relative inline-flex items-baseline font-extrabold tracking-tight text-2xl"
    style={{ fontFamily: "Manrope, sans-serif", letterSpacing: "-0.03em", color: C.teal }}>
    Cl<span className="relative inline-block">ı<span aria-hidden className="absolute left-1/2 -translate-x-1/2 rounded-full clinq-dot-glow"
      style={{ backgroundColor: C.orange, height: "0.5rem", width: "0.5rem", bottom: "1.05em" }}></span></span>nq
  </span>
);

export default function PitchPublic() {
  const { deckId } = useParams();
  const [deck, setDeck] = useState(null);
  const [error, setError] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfDone, setPdfDone] = useState(false);

  const downloadPdf = async () => {
    setPdfBusy(true);
    setPdfDone(false);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/pitch/public/${deckId}/pdf`;
      const started = Date.now();
      let res = await axios.get(url, { responseType: "blob" });
      // 202 = render in progress server-side — retry until the PDF is ready
      while (res.status === 202) {
        if (Date.now() - started > 300000) throw new Error("PDF is taking longer than expected — please try again shortly.");
        await new Promise((r) => setTimeout(r, 2500));
        res = await axios.get(url, { responseType: "blob" });
      }
      const objUrl = URL.createObjectURL(res.data);
      setPdfDone(true);
      await new Promise((r) => setTimeout(r, 700));
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `${(deck?.company_name || "deck").replace(/\s+/g, "_")}_pitch_deck.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objUrl), 5000);
    } catch (e) {
      setError(null); // don't nuke the page — surface inline on the button title only
      alert(e?.message || "PDF download failed — please try again.");
    }
    setPdfBusy(false);
  };

  useEffect(() => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/pitch/public/${deckId}`;
    axios.get(url).then(({ data }) => setDeck(data)).catch((e) => setError(extractErrorMessage(e, "Deck not found")));
  }, [deckId]);

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center text-white px-6" style={{ backgroundColor: C.bgDark, fontFamily: "Manrope, sans-serif" }}>
        <div className="text-center max-w-md" data-testid="public-deck-error">
          <ClinqMark />
          <h1 className="text-3xl font-extrabold mt-6">This deck isn&apos;t shared</h1>
          <p className="text-white/55 mt-2">The link may be wrong or the owner hasn&apos;t enabled public sharing.</p>
          <Link to="/" className="inline-block mt-6 px-4 py-2 rounded-md font-semibold text-sm"
            style={{ backgroundColor: C.teal, color: "#fff" }}>Back to Clinq</Link>
        </div>
      </div>
    );
  }
  if (!deck) {
    return (
      <div className="min-h-screen grid place-items-center text-white/50" style={{ backgroundColor: C.bgDark, fontFamily: "Manrope, sans-serif" }} data-testid="public-deck-loading">
        Loading deck…
      </div>
    );
  }

  const brand = deck.brand_color || C.teal;
  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: C.bgDark, fontFamily: "Manrope, sans-serif" }} data-testid="public-deck-page">
      <ExportProgressOverlay active={pdfBusy} done={pdfDone}
        title="Preparing your PDF"
        subtitle="Rendering print-quality pages — usually 10–30 seconds."
        testid="public-pdf-export-progress" />
      <header style={{ backgroundColor: "#000" }} className="border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/"><ClinqMark /></Link>
          <div className="flex items-center gap-4">
            {deck.pdf_available && (
              <button
                onClick={downloadPdf}
                disabled={pdfBusy}
                data-testid="public-deck-download-pdf-btn"
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md text-[12px] font-bold transition-colors"
                style={{ backgroundColor: pdfBusy ? "rgba(245,158,11,0.5)" : C.orange, color: "#050E0D" }}
              >
                <FilePdf size={14} weight="bold" /> {pdfBusy ? "Preparing…" : "Download deck (PDF)"}
              </button>
            )}
            <div className="text-xs uppercase tracking-[0.22em] font-semibold text-white/40 inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: brand }}></span> Read-only deck
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 sm:px-8 py-12">
        {/* Cover */}
        <section className="rounded-2xl border p-10 mb-8" data-testid="public-deck-cover"
          style={{ backgroundColor: C.panel, borderColor: "rgba(123, 211, 206, 0.18)" }}>
          {deck.logo && (
            <div className="rounded-md bg-white/95 p-4 inline-grid place-items-center mb-6" style={{ minHeight: "60px" }}>
              <img src={deck.logo} alt="Logo" className="max-h-12 object-contain" />
            </div>
          )}
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] mb-3" style={{ color: brand }}>
            {(deck.industry || "—")} · {(deck.funder_type || "—").replace("_", " ")}
          </div>
          <h1 className="text-5xl font-extrabold tracking-tight" style={{ letterSpacing: "-0.03em" }}>{deck.company_name}</h1>
          {deck.one_liner && <p className="text-white/65 mt-4 max-w-3xl text-lg leading-relaxed">{deck.one_liner}</p>}
        </section>

        {/* Slides — GTM (slide 7) is hidden per iter56. */}
        {(deck.slides || []).filter((s) => s.num !== 7).map((s) => (
          <section key={s.num} className="rounded-2xl border p-8 mb-6" data-testid={`public-slide-${s.num}`}
            style={{ backgroundColor: C.panel, borderColor: "rgba(123, 211, 206, 0.12)" }}>
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] flex items-center gap-2" style={{ color: brand }}>
              <span>Slide {String(s.num).padStart(2, "0")}</span>
              {s.pillar && <span className="px-2 py-0.5 rounded-full text-[9px]" style={{ backgroundColor: "rgba(123, 211, 206, 0.15)", color: C.tealSoft }}>{s.pillar}</span>}
            </div>
            <h2 className="text-2xl font-extrabold mt-2 tracking-tight" style={{ letterSpacing: "-0.02em" }}>{s.title}</h2>
            <div className="text-white/85 mt-4 leading-relaxed whitespace-pre-wrap text-sm">
              {s.content || <span className="text-white/30 italic">— no content —</span>}
            </div>
            {!!(s.images?.length) && (
              <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
                {s.images.map((img) => (
                  <img key={img.id} src={img.url} alt={img.caption || ""}
                    className="w-full rounded-md object-cover border" style={{ borderColor: "rgba(255,255,255,0.08)", maxHeight: "180px" }} />
                ))}
              </div>
            )}
            {!!(s.extras?.length) && (
              <div className="mt-6 space-y-4">
                {s.extras.map((x) => (
                  <div key={x.id} className="rounded-md border p-4" style={{ borderColor: "rgba(123, 211, 206, 0.12)", backgroundColor: "rgba(0,0,0,0.25)" }}>
                    <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-white/45">{x.title || "Continued"}</div>
                    <div className="text-white/80 mt-2 leading-relaxed whitespace-pre-wrap text-sm">{x.content || ""}</div>
                  </div>
                ))}
              </div>
            )}
          </section>
        ))}

        <footer className="text-center text-white/35 text-xs mt-12 pb-6">
          <Sparkle size={12} weight="fill" className="inline mr-1" style={{ color: C.orange }} />
          Made with Clinq · Get your business funder-ready at <Link to="/" className="underline">clinq.africa</Link>
        </footer>
      </main>
    </div>
  );
}
